import { Buffer } from "buffer";
import jo from "jpeg-autorotate";
import fileService from '../services/fileService';
import { captureException, reportError } from '../sentry';
import { UPLOAD_CONFIG } from '../constants/upload';
import { putBlobToPresignedUrl } from './s3DirectUpload';
import { runWithConcurrency } from './runWithConcurrency';
import { getMultipartConcurrency } from './deviceUtils';
import { isVideo } from './function';

const MIN_PART_SIZE = UPLOAD_CONFIG.MIN_PART_SIZE;
const MAX_PART_SIZE = UPLOAD_CONFIG.MAX_PART_SIZE;
const MAX_PARTS = UPLOAD_CONFIG.MAX_PARTS;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_IMAGE_WIDTH = 1028;
const MAX_IMAGE_HEIGHT = 720;
const IMAGE_QUALITY = 0.75; // 75% quality
const BANNER_QUALITY = 0.8; // 80% quality for banners

/**
 * Validates if a file key is valid (not empty, not already a URL)
 * @param {string} key - The file key to validate
 * @returns {boolean} - True if the key is valid
 */
const isValidFileKey = (key) => {
  return key && typeof key === 'string' && key.trim() !== '' && !key.startsWith('http');
};

/**
 * Get content type based on file extension
 * @param {File} file - The file to get content type for
 * @returns {string} - The content type of the file
 */
export const getContentType = (file) => {
  return fileService.getContentType(file);
};

/**
 * Determines whether a file should use multipart upload instead of a single PUT.
 * @param {File|Blob} file
 * @returns {boolean}
 */
export const shouldUseMultipartUpload = (file) => {
  if (!file) {
    return false;
  }

  if (isVideo(file)) {
    return true;
  }

  return file.size > UPLOAD_CONFIG.SINGLE_PUT_MAX_BYTES;
};

/**
 * Upload file to S3 using presigned URL with timeout, retry, and progress reporting.
 * @param {string} presignedUrl - The presigned URL to upload to
 * @param {File|Blob} file - The file to upload
 * @param {string} contentType - The content type of the file
 * @param {function} onProgress - Callback function for upload progress (0–100)
 * @param {boolean} isPublic - Whether to set public-read ACL
 * @param {Object} [options] - Additional upload options (signal, maxRetries)
 * @returns {Promise<string>} - The permanent URL of the uploaded file
 */
export const uploadFileToS3 = async (
  presignedUrl,
  file,
  contentType,
  onProgress,
  isPublic = false,
  options = {}
) => {
  const { permanentUrl } = await putBlobToPresignedUrl(presignedUrl, file, contentType, {
    isPublic,
    onProgress: onProgress
      ? (event) => onProgress(event.percent)
      : undefined,
    ...options,
  });

  return permanentUrl;
};

/**
 * Creates file data object with file, preview, and base64
 * @param {File} file - The file object
 * @param {string} preview - The preview URL
 * @returns {Object} File data object
 */
export  const createFileData = (file, preview) => ({
  file,
  preview,
  base64: preview
});

/**
 * Process banner image file (compress but don't resize to maintain dimensions)
 * @param {File} file - The banner image file to process
 * @returns {Promise<File>} - Processed banner image file
 */
export const processBannerImage = async (file) => {
  // if (!file.type.startsWith("image/")) {
  //   return file;
  // }

  try {
    // Check file size
    // if (file.size > MAX_IMAGE_SIZE) {
    //   throw new Error("You can only upload up to 10mb image size.");
    // }

    // Convert File to Buffer
    const arrayBuffer = await file.arrayBuffer();
    const buffer = Buffer.from(arrayBuffer);

    // Fix rotation using EXIF info
    const tempImgBuffer = await jo.rotate(buffer, { quality: 100 })
      .then(({ buffer }) => buffer)
      .catch(() => buffer);

    // Create a blob from the buffer
    const blob = new Blob([tempImgBuffer], { type: file.type });
    const imageUrl = URL.createObjectURL(blob);

    // Process image using Canvas (only compress, don't resize)
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        // Keep original dimensions for banners
        const width = img.width;
        const height = img.height;

        // Create canvas and draw image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob with quality (slightly lower quality for banners to reduce file size)
        canvas.toBlob(
          (blob) => {
            // Create new file from blob
            const processedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: file.lastModified
            });
            URL.revokeObjectURL(imageUrl);
            resolve(processedFile);
          },
          file.type,
          BANNER_QUALITY
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error('Failed to load image'));
      };

      img.src = imageUrl;
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Process image file (compress and resize if needed)
 * @param {File} file - The image file to process
 * @returns {Promise<File>} - Processed image file
 */
export const processImage = async (file) => {
  // if (!file.type.startsWith("image/")) {
  //   return file;
  // }

  try {
    // Check file size
    // if (file.size > MAX_IMAGE_SIZE) {
    //   throw new Error("You can only upload up to 10mb image size.");
    // }

    // Convert File to Buffer
    const arrayBuffer = await file.arrayBuffer();
    const buffer = Buffer.from(arrayBuffer);

    // Fix rotation using EXIF info
    const tempImgBuffer = await jo.rotate(buffer, { quality: 100 })
      .then(({ buffer }) => buffer)
      .catch(() => buffer);

    // Create a blob from the buffer
    const blob = new Blob([tempImgBuffer], { type: file.type });
    const imageUrl = URL.createObjectURL(blob);

    // Process image using Canvas
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;

        if (width > MAX_IMAGE_WIDTH || height > MAX_IMAGE_HEIGHT) {
          const aspectRatio = width / height;
          if (height * aspectRatio > MAX_IMAGE_WIDTH) {
            height = Math.floor(MAX_IMAGE_WIDTH / aspectRatio);
            width = MAX_IMAGE_WIDTH;
          } else {
            width = Math.floor(MAX_IMAGE_HEIGHT * aspectRatio);
            height = MAX_IMAGE_HEIGHT;
          }
        }

        // Create canvas and draw image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob with quality
        canvas.toBlob(
          (blob) => {
            // Create new file from blob
            const processedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: file.lastModified
            });
            URL.revokeObjectURL(imageUrl);
            resolve(processedFile);
          },
          file.type,
          IMAGE_QUALITY
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error('Failed to load image'));
      };

      img.src = imageUrl;
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Common utility function to process data with signed URLs
 * @param {Object} response - The API response
 * @param {string} dataPath - The path to the data array
 * @param {Function} extractFileKeys - Function to extract file keys from items
 * @param {Function} replaceFileKeys - Function to replace file keys with signed URLs
 * @param {boolean} isProfile - Whether these are profile files
 * @returns {Promise<Object>} - Modified response with signed URLs
 */
const processDataWithSignedUrls = async (response, dataPath, extractFileKeys, replaceFileKeys, isProfile = false) => {
  try {
    // Navigate to the data array using the dataPath
    const pathParts = dataPath.split('.');
    let dataArray = response.data;
    
    for (const part of pathParts) {
      if (dataArray && dataArray[part]) {
        dataArray = dataArray[part];
      } else {
        return response;
      }
    }
    
    if (!Array.isArray(dataArray)) {
      return response;
    }
    
    // Extract file keys using the provided function
    const fileKeys = extractFileKeys(dataArray);
    
    if (fileKeys.length === 0) {
      return response;
    }
    
    // Remove duplicates
    const uniqueFileKeys = [...new Set(fileKeys)];
    
    // Fetch signed URLs
    const signedUrlsData = await getSignedUrls(uniqueFileKeys, isProfile);
    
    // Create a map of file keys to signed URLs
    const signedUrlMap = {};
    signedUrlsData.forEach(item => {
      signedUrlMap[item.key] = item.signedUrl;
    });
    
    // Replace file keys using the provided function
    const modifiedDataArray = replaceFileKeys(dataArray, signedUrlMap);
    
    // Create modified response
    const modifiedResponse = { ...response };
    let currentLevel = modifiedResponse.data;
    
    // Navigate to the correct level and replace the data array
    for (let i = 0; i < pathParts.length - 1; i++) {
      currentLevel = currentLevel[pathParts[i]];
    }
    
    currentLevel[pathParts[pathParts.length - 1]] = modifiedDataArray;
    
    return modifiedResponse;
  } catch (error) {
    reportError(error, 'process data with signed URLs');
    return response;
  }
};

/**
 * Process users data by replacing profile_picture file keys with signed URLs
 * @param {Object} response - The API response containing users data
 * @param {string} dataPath - The path to the users array in the response (default: 'users')
 * @returns {Promise<Object>} - Modified response with signed URLs
 */
export const processUsersWithSignedUrls = async (response, dataPath = 'users') => {
  const extractFileKeys = (usersArray) => 
    usersArray
      .map(user => user.profile_picture)
      .filter(isValidFileKey);
  
  const replaceFileKeys = (usersArray, signedUrlMap) =>
    usersArray.map(user => ({
      ...user,
      profile_picture: signedUrlMap[user.profile_picture] || user.profile_picture
    }));
  
  return processDataWithSignedUrls(response, dataPath, extractFileKeys, replaceFileKeys, true);
};

/**
 * Upload large file using multipart upload with bounded concurrency and byte-level progress.
 * @param {File} file - The file to upload
 * @param {function} onProgress - Progress callback function (0–100 transfer progress)
 * @param {Object} [options]
 * @param {boolean} [options.processImage=false] - Compress/resize images before upload
 * @param {AbortSignal} [options.signal] - Optional abort signal
 * @returns {Promise<{key: string, thumbnailUrl?: string}>}
 */
export const uploadLargeFile = async (file, onProgress, options = {}) => {
  const { processImage: shouldProcessImage = false, signal } = options;

  try {
    const processedFile =
      shouldProcessImage && file?.type?.startsWith('image/')
        ? await processImage(file)
        : file;

    const contentType = getContentType(processedFile);
    const fileSize = processedFile.size;

    let partSize = MIN_PART_SIZE;
    const fileSizeMB = fileSize / (1024 * 1024);

    if (fileSizeMB > 5120) {
      partSize = Math.min(20 * 1024 * 1024, MAX_PART_SIZE);
    } else if (fileSizeMB > 1024) {
      partSize = Math.min(10 * 1024 * 1024, MAX_PART_SIZE);
    }

    let partCount = Math.ceil(fileSize / partSize);
    if (partCount > MAX_PARTS) {
      partSize = Math.ceil(fileSize / MAX_PARTS);
      partCount = MAX_PARTS;
    }

    const { uploadId, key, presignedUrls } = await getMultipartPresignedUrls(
      contentType,
      fileSize,
      partCount
    );

    const partLoadedBytes = new Array(presignedUrls.length).fill(0);

    const reportAggregateProgress = () => {
      if (!onProgress || fileSize <= 0) {
        return;
      }

      const loaded = partLoadedBytes.reduce((sum, value) => sum + value, 0);
      onProgress(Math.min(100, Math.round((loaded * 100) / fileSize)));
    };

    const uploadTasks = presignedUrls.map(({ url }, index) => async () => {
      const start = index * partSize;
      const end = Math.min(start + partSize, fileSize);
      const part = processedFile.slice(start, end);

      const partResult = await uploadPart(url, part, contentType, {
        signal,
        onProgress: (event) => {
          partLoadedBytes[index] = event.loaded;
          reportAggregateProgress();
        },
      });

      partLoadedBytes[index] = part.size;
      reportAggregateProgress();
      return partResult;
    });

    const parts = await runWithConcurrency(uploadTasks, getMultipartConcurrency());

    const completionResult = await completeMultipartUpload(key, uploadId, parts);
    return {
      key,
      ...(completionResult?.thumbnailUrl && { thumbnailUrl: completionResult.thumbnailUrl }),
    };
  } catch (error) {
    reportError(error, 'multipart upload');
    throw error;
  }
};

/**
 * Get signed URLs for a list of file keys
 * @param {string[]} fileKeys - Array of file keys to get signed URLs for
 * @param {boolean} isProfile - Whether these are profile files (true for profile pictures/banners, false for content)
 * @returns {Promise<Array<{key: string, signedUrl: string}>>} - Array of objects containing file keys and their signed URLs
 */
export const getSignedUrls = async (fileKeys, isProfile = false) => {
  try {
    const response = await fileService.getSignedUrls(fileKeys, isProfile);
    return response.data;
  } catch (error) {
    reportError(error, 'get signed URLs');
    throw new Error(`Failed to get signed URLs: ${error.message}`);
  }
};

/**
 * Process content data by replacing asset keys (and legacy media) with signed URLs.
 * Content uses assets for single or multiple image/video; 
 * @param {Object} response - The API response containing content data
 * @param {string} dataPath - The path to the content array in the response (e.g., 'data.content' or 'data')
 * @returns {Promise<Object>} - Modified response with signed URLs
 */
export const processContentWithSignedUrls = async (response, dataPath = 'data.content') => {
  try {
    // Navigate to the content array using the dataPath
    const pathParts = dataPath.split('.');
    let contentArray = response.data;
    
    for (const part of pathParts) {
      if (contentArray && contentArray[part]) {
        contentArray = contentArray[part];
      } else {
        // If the path doesn't exist, return original response
        return response;
      }
    }
    
    // Ensure we have an array of content items
    if (!Array.isArray(contentArray)) {
      return response;
    }
    
    // Extract all unique file keys from assets (and legacy media)
    const fileKeys = [];

    contentArray.forEach(item => {
      // Primary: use assets for all content (single or bundle)
      if (item.assets && Array.isArray(item.assets)) {
        item.assets.forEach(asset => {
          // Always sign the main asset key (image/video src)
          if (isValidFileKey(asset.key)) {
            fileKeys.push(asset.key);
          }
          // Also sign any poster/thumbnail keys for videos
          if (isValidFileKey(asset.poster)) {
            fileKeys.push(asset.poster);
          }
        });
      }
    });
    
    if (fileKeys.length === 0) {
      return response;
    }
    
    // Remove duplicates
    const uniqueFileKeys = [...new Set(fileKeys)];
    
    // Fetch signed URLs for all file keys
    const signedUrlsData = await getSignedUrls(uniqueFileKeys, false);
    
    // Create a map of file keys to signed URLs for quick lookup
    const signedUrlMap = {};
    signedUrlsData.forEach(item => {
      signedUrlMap[item.key] = item.signedUrl;
    });
    
    // Replace asset keys and legacy media with signed URLs; output media[].src
    const modifiedContentArray = contentArray.map(item => {
      const modifiedItem = { ...item };

      // Replace keys in assets with signed URLs (all content uses assets now)
      if (item.assets && Array.isArray(item.assets)) {
        modifiedItem.assets = item.assets.map(asset => ({
          ...asset,
          key: signedUrlMap[asset.key] || asset.key,
          poster: signedUrlMap[asset.poster] || asset.poster
        }));

        // Build media array for BundleSlider when multiple assets (bundle)
        const isBundle = item.type_content === 'bundle' || item.type_file === 'bundle' ||
          item.content_type === 'bundle' || item.isBundle || item.assets.length > 1;
        if (isBundle) {
          modifiedItem.media = modifiedItem.assets.map(asset => ({
            src: asset.key,
            type_file: asset.type,
            caption: item.caption,
            duration: asset.duration,
            poster: asset.poster
          }));
        }
      }

      return modifiedItem;
    });
    
    // Create modified response with signed URLs
    const modifiedResponse = { ...response };
    let currentLevel = modifiedResponse.data;
    
    // Navigate to the correct level and replace the content array
    for (let i = 0; i < pathParts.length - 1; i++) {
      currentLevel = currentLevel[pathParts[i]];
    }
    
    currentLevel[pathParts[pathParts.length - 1]] = modifiedContentArray;
    
    return modifiedResponse;
  } catch (error) {
    reportError(error, 'process content with signed URLs');
    // If processing fails, return original response
    return response;
  }
};

/**
 * Process content creator data by replacing profile_picture file keys with signed URLs
 * @param {Object} response - The API response containing creator data
 * @param {string} dataPath - The path to the creators array in the response (default: 'data')
 * @returns {Promise<Object>} - Modified response with signed URLs
 */
export const processCreatorsWithSignedUrls = async (response, dataPath = 'data') => {
  const extractFileKeys = (creatorsArray) => 
    creatorsArray
      .map(creator => creator.profile_picture)
      .filter(isValidFileKey);
  
  const replaceFileKeys = (creatorsArray, signedUrlMap) =>
    creatorsArray.map(creator => ({
      ...creator,
      profile_picture: signedUrlMap[creator.profile_picture] || creator.profile_picture
    }));
  
  return processDataWithSignedUrls(response, dataPath, extractFileKeys, replaceFileKeys, true);
};




// TODO: after sometime, remove this function (before remove check the usage of this function)
/**
 * Get presigned URL for file upload (without authentication)
 * @param {File} file - The file to get presigned URL for
 * @param {boolean} isProfile - Whether this is a profile upload (true for profile pictures/banners, false for content)
 * @param {string} module - Module name for the upload
 * @returns {Promise<{url: string, key: string}>} - Object containing presigned URL and file key
 */
export const getPresignedUrlWithoutAuth = async (file, isProfile = false, module) => {
  try {
    const response = await fileService.getPresignedUrlWithoutAuth(file, isProfile, module);
    return response.data;
  } catch (error) {
    reportError(error, 'presigned URL without auth');
    throw new Error(`Failed to get presigned URL: ${error.message}`);
  }
};

/**
 * Get presigned URL for file upload
 * @param {File} file - The file to get presigned URL for
 * @param {boolean} isProfile - Whether this is a profile upload (true for profile pictures/banners, false for content)
 * @returns {Promise<{url: string, key: string}>} - Object containing presigned URL and file key
 */
export const getPresignedUrl = async (file, isProfile = false) => {
  try {
    const response = await fileService.getPresignedUrl(file, isProfile);
    return response.data;
  } catch (error) {
    reportError(error, 'presigned URL');
    throw new Error(`Failed to get presigned URL: ${error.message}`);
  }
};

/**
 * Get presigned URLs for multipart upload
 * @param {string} contentType - The content type of the file
 * @param {number} fileSize - Size of the file in bytes
 * @param {number} partCount - Number of parts to split the file into
 * @returns {Promise<{uploadId: string, key: string, presignedUrls: Array<{partNumber: number, url: string}>}>}
 */
export const getMultipartPresignedUrls = async (contentType, fileSize, partCount) => {
  try {
    const response = await fileService.getMultipartPresignedUrls(contentType, fileSize, partCount);
    return response.data;
  } catch (error) {
    reportError(error, 'multipart presigned URLs');
    throw new Error(`Failed to get multipart presigned URLs: ${error.message}`);
  }
};

/**
 * Upload a file part to S3 via direct presigned PUT (no application auth headers).
 * @param {string} url - Presigned URL for the part
 * @param {Blob} part - The file part to upload
 * @param {string} contentType - Content type of the file
 * @param {Object} [options] - Upload options forwarded to putBlobToPresignedUrl
 * @returns {Promise<{ETag: string, PartNumber: number}>}
 */
export const uploadPart = async (url, part, contentType, options = {}) => {
  try {
    const { etag } = await putBlobToPresignedUrl(url, part, contentType, options);
    const partNumber = parseInt(new URL(url).searchParams.get('partNumber'), 10);

    if (!etag) {
      throw new Error('No ETag received from S3');
    }

    return {
      ETag: etag,
      PartNumber: partNumber,
    };
  } catch (error) {
    captureException(error, {
      tags: { context: 'uploadPart' },
      extra: {
        responseData: error.response?.data,
        requestUrl: url,
        partSize: part?.size,
        contentType,
      },
    });
    throw error;
  }
};

/**
 * Complete multipart upload
 * @param {string} key - The file key
 * @param {string} uploadId - The upload ID
 * @param {Array<{ETag: string, PartNumber: number}>} parts - Array of uploaded parts
 * @returns {Promise<{key: string}>}
 */
export const completeMultipartUpload = async (key, uploadId, parts) => {
  try {
    const response = await fileService.completeMultipartUpload(key, uploadId, parts);
    return response.data;
  } catch (error) {
    reportError(error, 'complete multipart upload');
    throw new Error(`Failed to complete multipart upload: ${error.message}`);
  }
};

/**
 * Process messages data by replacing profile_picture file keys with signed URLs in nested dest objects
 * @param {Object} response - The API response containing messages data
 * @param {string} dataPath - The path to the messages array in the response (default: 'messages')
 * @returns {Promise<Object>} - Modified response with signed URLs
 */
export const processMessagesWithSignedUrls = async (response, dataPath = 'messages') => {
  const extractFileKeys = (messagesArray) => 
    messagesArray
      .map(message => message.dest?.profile_picture)
      .filter(isValidFileKey);
  
  const replaceFileKeys = (messagesArray, signedUrlMap) =>
    messagesArray.map(message => ({
      ...message,
      dest: message.dest ? {
        ...message.dest,
        profile_picture: signedUrlMap[message.dest.profile_picture] || message.dest.profile_picture
      } : message.dest
    }));
  
  return processDataWithSignedUrls(response, dataPath, extractFileKeys, replaceFileKeys, true);
};

/**
 * Process feed data by replacing profile_picture file keys with signed URLs in nested profile objects
 * @param {Object} response - The API response containing feed data
 * @param {string} dataPath - The path to the feed array in the response (default: 'data')
 * @returns {Promise<Object>} - Modified response with signed URLs
 */
export const processFeedWithSignedUrls = async (response, dataPath = 'data') => {
  const extractFileKeys = (feedArray) => 
    feedArray
      .map(item => item.profile?.profile_picture)
      .filter(isValidFileKey);
  
  const replaceFileKeys = (feedArray, signedUrlMap) =>
    feedArray.map(item => ({
      ...item,
      profile: item.profile ? {
        ...item.profile,
        profile_picture: signedUrlMap[item.profile.profile_picture] || item.profile.profile_picture
      } : item.profile
    }));
  
  return processDataWithSignedUrls(response, dataPath, extractFileKeys, replaceFileKeys, true);
};

/**
 * Process subscription data by replacing profile_picture file keys with signed URLs in nested ccreator objects
 * @param {Object} response - The API response containing subscription data
 * @param {string} dataPath - The path to the subscriptions array in the response (default: 'data')
 * @returns {Promise<Object>} - Modified response with signed URLs
 */
export const processSubscriptionsWithSignedUrls = async (response, dataPath = 'data') => {
  const extractFileKeys = (subscriptionsArray) => 
    subscriptionsArray
      .map(subscription => subscription.ccreator?.profile_picture)
      .filter(isValidFileKey);
  
  const replaceFileKeys = (subscriptionsArray, signedUrlMap) =>
    subscriptionsArray.map(subscription => ({
      ...subscription,
      ccreator: subscription.ccreator ? {
        ...subscription.ccreator,
        profile_picture: signedUrlMap[subscription.ccreator.profile_picture] || subscription.ccreator.profile_picture
      } : subscription.ccreator
    }));
  
  return processDataWithSignedUrls(response, dataPath, extractFileKeys, replaceFileKeys, true);
};

/**
 * Process message lines data by replacing image and video keys with signed URLs in content objects
 * @param {Object} response - The API response containing message lines data
 * @param {string} dataPath - The path to the messages_lines array in the response (default: 'messages_lines')
 * @returns {Promise<Object>} - Modified response with signed URLs
 */
export const processMessageLinesWithSignedUrls = async (response, dataPath = 'messages_lines') => {
  const extractFileKeys = (messageLinesArray) => {
    const fileKeys = [];
    messageLinesArray.forEach(messageLine => {
      if (messageLine.content) {
        // Process images
        if (messageLine.content.images && Array.isArray(messageLine.content.images)) {
          messageLine.content.images.forEach(imageKey => {
            if (isValidFileKey(imageKey)) {
              fileKeys.push(imageKey);
            }
          });
        }
        
        // Process videos
        if (messageLine.content.videos && Array.isArray(messageLine.content.videos)) {
          messageLine.content.videos.forEach(videoKey => {
            if (isValidFileKey(videoKey)) {
              fileKeys.push(videoKey);
            }
          });
        }
      }
    });
    return fileKeys;
  };
  
  const replaceFileKeys = (messageLinesArray, signedUrlMap) =>
    messageLinesArray.map(messageLine => ({
      ...messageLine,
      content: messageLine.content ? {
        ...messageLine.content,
        images: messageLine.content.images ? messageLine.content.images.map(imageKey => 
          signedUrlMap[imageKey] || imageKey
        ) : messageLine.content.images,
        videos: messageLine.content.videos ? messageLine.content.videos.map(videoKey => 
          signedUrlMap[videoKey] || videoKey
        ) : messageLine.content.videos
      } : messageLine.content
    }));
  
  return processDataWithSignedUrls(response, dataPath, extractFileKeys, replaceFileKeys, false);
};

/**
 * Process recipient data by replacing profile_picture file keys with signed URLs
 * @param {Object} recipient - The recipient object containing profile_picture
 * @returns {Promise<Object>} - Modified recipient with signed URL
 */
export const processRecipientWithSignedUrl = async (recipient) => {
  try {
    if (!recipient || !recipient.profile_picture) {
      return recipient;
    }

    const profilePicture = recipient.profile_picture;
    
    // Check if it's already a URL
    if (profilePicture.startsWith('http')) {
      return recipient;
    }

    // Get signed URL for profile picture
    const signedUrlsData = await getSignedUrls([profilePicture], true); // isProfile = true for profile pictures
    
    if (signedUrlsData.length > 0) {
      return {
        ...recipient,
        profile_picture: signedUrlsData[0].signedUrl
      };
    }

    return recipient;
  } catch (error) {
    reportError(error, 'process recipient with signed URL');
    // If processing fails, return original recipient
    return recipient;
  }
}; 